import { onPublicEvent, PublicEvents } from '@project/shared/utils/events/js/events';
import { createModule, startNode } from 'terrific-singleton';
import { TerrificSpec } from 'terrific-singleton/terrific-module';
import * as Track from '@project/shared/utils/tracking/js/tracking';

const classes = {
    container: 'js-m-download-table__container',
	counterFooter: 'js-download-table__counter-footer',
    filterItem: 'js-m-download-table__filter-item',
    listItem: 'js-m-download-table__item',
    loadMoreButton: 'js-load-more-button',
    groupWrapper: 'js-m-download-table__group',
    stateHidden: 'state-m-download-table__hidden',
    stateItemHidden: 'state-m-download-table__item-hide',
    descText: 'm-download-table__item-description',
    stateDescExpanded: 'status-m-download-table__item-description--expanded',
    descShowBtn: 'js-m-download-table__show-description',
    stateBtnVisible: 'status-m-download-table__show-description--visible',
    gatedItem: 'm-download-table__item--gated',
    inputKeywordSearch: 'm-filter-keywordsearch__input',
};

const dataAttributes = {
    apiEndpoint: 'api-endpoint',
    configuration: 'configuration',
    filterConfiguration: 'filters',
};

export interface IDownloadTableOptions {
	apiEndpoint: string;
	configuration: string;
	filters: {id:string}[];
}

interface IDownloadTableFilter {
	$element: JQuery<HTMLElement>,
	id: string,
}

@createModule('DownloadTable')
/**
 * download-table module implementation.
 */
export class DownloadTable extends TerrificSpec {
    private $ctx: JQuery<HTMLElement>;

    private currentCount: number;
	private totalCount: number;

	private filters: IDownloadTableFilter[];
    private options: IDownloadTableOptions;

	async start(resolve: Function) {
		this.$ctx = $(this._ctx);

		this.options = {
			apiEndpoint: this.$ctx.data(dataAttributes.apiEndpoint),
			configuration: this.$ctx.data(dataAttributes.configuration),
			filters: this.$ctx.data(dataAttributes.filterConfiguration),
		};
		this.filters = this.createFilters();

		// Assign events
        this.$ctx.on('click', `.${classes.descShowBtn}`, this.showDescription.bind(this));
		this.$ctx.on('click', `.${classes.loadMoreButton}`, this.loadMore.bind(this));

        for (let i = 0; i < this.filters.length; i++) {
            const filter = this.filters[i];
            onPublicEvent(PublicEvents.uxChange, filter.$element[0], (event: Event, data: object) => {
                this.facetAndList();
            });
        }

		// Initiate
		this.updateLoadMore(false);
		this.facetAndList();

		resolve();
	}

    stop() {
    }

	private createFilters(): IDownloadTableFilter[]{
		let results: IDownloadTableFilter[] = [];

		for (let i = 0; i < this.options.filters.length; i++) {
			const filter = this.options.filters[i];
			const element = document.getElementById(filter.id);
			if (element != null) {
				results.push({
					$element: $(element),
					id: filter.id
				});
			}
		}

		return results;
	}

	private getSelectedFacets(): { id: string, value: string }[] {
		let results: { id: string, value: string }[] = [];

		for (let i = 0; i < this.filters.length; i++) {
			const filter = this.filters[i];
			const value = filter.$element.val();
			if (value) {
				results.push({ id: filter.id, value: <string>value });
			}
		}

		return results;
	}

    private facetAndList(): void {
        this.$ctx.find(`.${classes.inputKeywordSearch}`).val("");
        const payload = {
            configuration: this.options.configuration,
            facets: this.getSelectedFacets(),
            offset: 0
        };

        $.ajax({
            type: 'POST',
            url: this.options.apiEndpoint,
            data: payload,
            dataType: 'html'
        }).done((response: string, status: JQuery.Ajax.SuccessTextStatus, jqXhr: JQuery.jqXHR<any>) => {

            const container = this.$ctx.find(`.${classes.container}`);
            container.html(response);

            // Look for a script tag containing extra data.
            const data = container.find("script[type='application/json']");
            if (data.length > 0) {
                const json = data[0].innerHTML;
                const jsonData = JSON.parse(json);

				this.updateCounters(jsonData.count, jsonData.displayed, jsonData.footer);
                this.updateLoadMore(jsonData.showMore);
                this.initializeComponents();

                this.RegisteredUpdate();

                // Remove the script tag.
                data.remove();
            } else {
                // No response data.
                this.updateLoadMore(false);
            }
        });
    }

	private loadMore(): void {
        this.$ctx.find(`.${classes.inputKeywordSearch}`).val("");
        const payload = {
            configuration: this.options.configuration,
            facets: this.getSelectedFacets(),
            offset: this.currentCount,
        }

        $.ajax({
            type: 'POST',
            url: this.options.apiEndpoint,
            data: payload,
            dataType: 'html'
        }).done((response: string, status: JQuery.Ajax.SuccessTextStatus, jqXhr: JQuery.jqXHR<any>) => {

            const container = this.$ctx.find(`.${classes.container}`);
            container.append(response);

            // Look for a script tag containing extra data.
            const data = container.find("script[type='application/json']");
            if (data.length > 0) {
                const json = data[0].innerHTML;
                const jsonData = JSON.parse(json);

				this.updateCounters(jsonData.count, jsonData.displayed, jsonData.footer);
				this.updateLoadMore(jsonData.showMore);
                this.initializeComponents();
                this.initializeShowMoreLinks();

                this.RegisteredUpdate();

                // Remove the script tag.
                data.remove();
            } else {
                // No response data.
                this.updateLoadMore(false);
            }
        });
    }

	private updateCounters(count: number, displayed: number, footer: string): void {
		this.currentCount = displayed,
		this.totalCount = count;
		this.$ctx.find(`.${classes.counterFooter}`).text(footer);
        this.$ctx.find(`.${classes.counterFooter}`).attr("data-currentcount", this.currentCount)
	}

    private updateLoadMore(enabled: boolean): void {
        if (enabled)
            this.$ctx.find(`.${classes.loadMoreButton}`).show();
        else
            this.$ctx.find(`.${classes.loadMoreButton}`).hide();
    }

    private initializeComponents(): void{
        // Find all non-initialized terrific components in the container
        const terrificSelector = '[data-t-name="DownloadItem"]:not([data-t-id])';
        this.$ctx.find(`.${classes.container}`).find(terrificSelector).each((i,e) => {
            startNode(e);
        });
    }

    private initializeShowMoreLinks(): void {
        const $descText = this.$ctx.find(`.${classes.descText}`);
        setTimeout(() => {
            for (let i = 0; i < $descText.length; i++) {
                const instance = $descText[i];
                if (instance.scrollHeight > 44) {
                    $(instance).next().addClass(classes.stateBtnVisible);
                };
            }
        }, 100);
    }

    private showDescription(e: JQuery.Event): void {
        $(e.currentTarget).prev().addClass(classes.stateDescExpanded);
        $(e.currentTarget).removeClass(classes.stateBtnVisible);
    }

    private RegisteredUpdate() {
		const $gatedItem = this.$ctx.find(`.${classes.gatedItem}`);
		if(Track.isKnownMarketoLead()) {
            $gatedItem.each((i,e) => {
                $(e).removeClass('m-download-table__item--locked');
            });
		}
		if(Track.isKnownCdcLead()) {
            $gatedItem.each((i,e) => {
                $(e).removeClass('m-download-table__item--locked');
            });
		}
	}
}
